/**
 * Fetch one message file's bytes, authenticated (#1069 Story 4.2, AC 12).
 *
 * ⚠️ THIS EXISTS BECAUSE C1 IS WRONG FOR AN AUTHENTICATED ROUTE, and the
 * running app is what proved it (2026-09-14). C1 says the viewers "already take
 * a URL", so FR16 needs no viewer changes — true, but only for a URL the
 * browser can fetch on its own. The serving route sits behind `requireAuth`,
 * and this app authenticates with a JWT COOKIE: an `<img src>` pointing at the
 * API's origin sends no cookie cross-origin and no `Authorization` header ever,
 * so the viewer opened with the right title over "Failed to load image".
 *
 * ⚠️ BASE64, NOT A BLOB URL, AND THAT IS THE CROSS-PLATFORM ANSWER. The house
 * path for every other attachment (the carousel's default source →
 * `attachmentService.getAttachment`) hands the viewer base64 for exactly this
 * reason: a `blob:` URL is a web concept, and React Native's `Image` cannot
 * open one. Following the house pattern keeps one code path for web, iOS and
 * Android instead of a `.web`/`.native` split for something neither platform
 * needs to differ on.
 *
 * ⚠️ THE COST IS HONEST AND IT IS THE SAME COST THE PRODUCT ALREADY PAYS. A
 * 25 MB file becomes roughly 33 MB of base64 in memory while the viewer is
 * open. `attachmentService` does this today for request attachments; doing it
 * differently here would mean two answers to one question.
 */
import { buildApiHeaders } from '../api';
import { messageFileUrl } from './messageFileUrl';

/** What the viewers need: the bytes, and what they are. */
export interface FetchedMessageFile {
  base64: string;
  mimeType: string;
  /**
   * The DECODED length, read from the response before encoding (Story 8.3,
   * C4). It is what the viewer's cache charges against its ceiling: the
   * base64 above is in an exact 4:3 ratio to it, so one number bounds both
   * and nobody has to walk the string to find out.
   */
  byteLength: number;
}

/** `ArrayBuffer` → base64, in chunks so a large file cannot blow the stack. */
const toBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  // ⚠️ CHUNKED. `String.fromCharCode(...bytes)` spreads every byte as an
  // argument, which throws `RangeError: Maximum call stack size exceeded`
  // somewhere around a hundred thousand of them — i.e. on any real photo.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return globalThis.btoa(binary);
};

export const fetchMessageFile = async (args: {
  threadId: string;
  messageId: string;
  fileId: string;
}): Promise<FetchedMessageFile> => {
  const headers = await buildApiHeaders();
  const response = await fetch(messageFileUrl(args), {
    method: 'GET',
    headers,
    // The session is a cookie; without this the request is anonymous and the
    // route answers 401.
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Could not load the file (${response.status}).`);
  }
  const buffer = await response.arrayBuffer();
  return {
    base64: toBase64(buffer),
    byteLength: buffer.byteLength,
    // The STORED, sniffed type — the server decides what this is, not the
    // caller and not the file name.
    mimeType:
      response.headers.get('content-type') ?? 'application/octet-stream',
  };
};
